import { DesignResource, useGetProjectsByProjectQuery, useLazyGetDesignsQuery } from '@/api/serverApi'
import BrowseUngroupedDesignsView from '@/components/browse-views/designs/ungrouped/BrowseUngroupedDesignsView'
import ViewPicker from '@/components/browse-views/view-picker/ViewPicker'
import NoDesignsDisplay from '@/components/empty-states/designs/NoDesignsDisplay'
import { Navbar } from '@/components/header'
import CreateButtonHeader from '@/components/header/CreateButtonHeader'
import DuplicateDesignModal from '@/components/modals/designs/DuplicateDesignModal'
import ReassignDesignModal from '@/components/modals/designs/ReassignDesignModal'
import UpdateProjectModal from '@/components/modals/projects/UpdateProjectModal'
import useSelectView from '@/hooks/useSelectView'
import { Button, Dropdown, LoadingSpinner, Meta, MinimalSelect, SearchIcon, TextInput, useInfinitePagination, useSearch, useSort } from '@cfs-fixings/shared'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

enum SortByOptions {
  alphabetical = 'alphabetical',
  newestFirst = 'newestFirst',
  oldestFirst = 'oldestFirst',
}

const textSpec = {
  header: 'Projects',
  subheader: 'Designs',
  searchPlaceholder: 'Search designs',
  sortByPrefix: 'Sort by',
  groupByPrefix: 'Group by',
  groupOptions: {
    allDesignsLabel: 'All Files',
    byProjectLabel: 'Project',
  },
  sortOptions: {
    alphabetical: 'Alphabetical',
    newestFirst: 'Newest First',
    oldestFirst: 'Oldest First',
  },
  renameProjectButtonLabel: 'Rename Project',
  dropdown: {
    duplicateButtonLabel: 'Duplicate',
    reassignButtonLabel: 'Assign to new Project',
  },
  noDesignDescription: 'Click below to create a new design under this project',
}

const sortOptions = [
  { value: SortByOptions.alphabetical, label: textSpec.sortOptions.alphabetical },
  { value: SortByOptions.newestFirst, label: textSpec.sortOptions.newestFirst },
  { value: SortByOptions.oldestFirst, label: textSpec.sortOptions.oldestFirst },
]

type DesignDropdownProps = {
  design: DesignResource
  onReset?: () => void
}

const DesignDropdown = ({ design, onReset }: DesignDropdownProps) => {
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false)
  const [isReassignOpen, setIsReassignOpen] = useState(false)

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="ml-auto" onClick={(e) => e.preventDefault()}>
        <Dropdown>
          <Dropdown.Item
            onClick={(e) => {
              e.stopPropagation()
              setIsDuplicateOpen(true)
            }}
          >
            {textSpec.dropdown.duplicateButtonLabel}
          </Dropdown.Item>
          <Dropdown.Item onClick={() => setIsReassignOpen(true)}>{textSpec.dropdown.reassignButtonLabel}</Dropdown.Item>
        </Dropdown>
      </div>
      <DuplicateDesignModal
        design={design}
        isOpen={isDuplicateOpen}
        onClose={() => setIsDuplicateOpen(false)}
        onSuccess={() => {
          setIsDuplicateOpen(false)
          onReset?.()
        }}
      />
      <ReassignDesignModal
        design={design}
        isOpen={isReassignOpen}
        onClose={() => setIsReassignOpen(false)}
        onSuccess={() => {
          setIsReassignOpen(false)
          onReset?.()
        }}
      />
    </>
  )
}

function ShowProjectPage () {
  const { projectId } = useParams<{ projectId: string }>()

  const [hasPerformedFirstLoad, setHasPerformedFirstLoad] = useState(false)
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)

  const { searchTerm, setSearchTerm } = useSearch()
  const { sortBy, setSortBy, sortDirection, setSortDirection } = useSort('updated', 'desc')
  const { viewType, setViewType } = useSelectView({})

  const debouncedSetSearchTerm = useDebouncedCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value),
    500,
  )

  const { data: projectResponse, isFetching: isProjectFetching } = useGetProjectsByProjectQuery({ project: projectId! })

  const [fetchDesignsUngrouped] = useLazyGetDesignsQuery({})

  const { data: designsData, isFetching: isDesignsFetching, ref: designsRef, reset: designsReset } = useInfinitePagination<DesignResource>({
    loadData: (page) => fetchDesignsUngrouped({
      projectId: projectId!,
      search: searchTerm,
      sortDirection: sortDirection,
      sortBy: sortBy,
      page: page,
      perPage: 45,
    }).then(res => {
      setHasPerformedFirstLoad(true)
      return res.data
    }),
  })


  useEffect(() => {
    // Any time the search or sort is changed we want to clear data and refetch
    designsReset()
  }, [designsReset, searchTerm, sortBy, sortDirection])

  return (
    <>
      <Meta title="Projects" />

      <Navbar
        topbar={
          <CreateButtonHeader>
            <h1 className="font-semibold text-[24px] text-gray-900">{textSpec.header}</h1>
          </CreateButtonHeader>
        }
      >
        <div className="flex flex-col w-full">
          <div className="flex flex-row justify-between items-center border-b border-gray-100 pb-4 mb-8 mx-2.5">
            {
              isProjectFetching
                ? <LoadingSpinner className="w-4 h-4" />
                : <h2 className="text-xl leading-6 font-semibold">{projectResponse?.data.name}</h2>
            }
            <Button
              variant="secondary"
              onClick={() => setIsUpdateModalOpen(true)}
            >
              <p className="text-nowrap">{textSpec.renameProjectButtonLabel}</p>
            </Button>
          </div>

          <h2 className="text-xl leading-6 font-medium pb-[21px] px-2.5">{textSpec.subheader}</h2>

          <div className="grid md:grid-cols-2 pb-4 gap-y-2 px-2.5">
            <TextInput
              defaultValue={searchTerm}
              prefixIcon={
                <SearchIcon className="w-[16px] h-[16px] text-gray-600" />
              }
              placeholder={textSpec.searchPlaceholder}
              onChange={debouncedSetSearchTerm}
            />
            <div className="flex md:flex-row flex-col md:justify-end md:items-center items-start justify-center gap-y-3">
              <div className="flex items-center justify-end gap-2.5 pr-[38px]">
                <p className="text-sm leading-[19.6px]">{textSpec.sortByPrefix}</p>
                <MinimalSelect
                  defaultValue={
                    sortBy === 'name'
                      ? SortByOptions.alphabetical
                      : sortBy === 'updated' && sortDirection === 'desc'
                        ? SortByOptions.newestFirst
                        : SortByOptions.oldestFirst
                  }
                  options={sortOptions}
                  onChange={(option) => {
                    if (option.value === SortByOptions.alphabetical) {
                      setSortBy('name')
                      setSortDirection('asc')
                    } else if (option.value === SortByOptions.newestFirst) {
                      setSortBy('updated')
                      setSortDirection('desc')
                    } else {
                      setSortBy('updated')
                      setSortDirection('asc')
                    }
                  }}
                />
              </div>
              <div className="self-end">
                <ViewPicker
                  selectedOption={viewType}
                  onOptionSelected={(viewType) => setViewType(viewType)}
                />
              </div>
            </div>
          </div>
          {
            designsData?.length === 0 && !isDesignsFetching && hasPerformedFirstLoad
              ? (
                <NoDesignsDisplay
                  projectId={projectId!}
                  descriptionOverride={textSpec.noDesignDescription}
                />
              )
              : null
          }
          <BrowseUngroupedDesignsView
            viewType={viewType}
            designs={designsData}
            footerElements={[
              <div key="project-pagination-ref" ref={designsRef} />,
            ]}
            isLoading={isDesignsFetching}
            dropdown={(design: DesignResource) => (
              <DesignDropdown
                design={design}
                onReset={() => {
                  // This is nasty, but necessary. The designs data needs to be manually reset
                  // as it's a lazy query, but if this happens at the same time as the cache invalidation
                  // it returns the cached data. This artificial delay addresses that.
                  // Related issue: https://github.com/reduxjs/redux-toolkit/issues/2802
                  setTimeout(() => {
                    designsReset()
                  }, 1000)
                }}
              />
            )}
          />
        </div>
      </Navbar>
      <UpdateProjectModal
        project={projectResponse?.data}
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
      />
    </>
  )
}

export default ShowProjectPage
